import { createContext, useContext, useState, useEffect } from 'react';

/**
 * Admin "Tweaks" state — palette (warm|slate|ink) and density (compact|regular|cozy).
 * Persisted to localStorage and applied as data-theme / data-density on the
 * .sb-admin shell wrapper. Dark/light "mode" lives in the shared ThemeContext
 * (so the side-rail dark toggle stays a single source of truth).
 *
 * Mirrors the reference Tweaks panel (SPEC §16).
 */
const TweaksContext = createContext(null);

export const PALETTES   = ['warm', 'slate', 'ink'];
export const DENSITIES  = ['compact', 'regular', 'cozy'];
export const NAV_MODES  = ['side', 'top'];

export function TweaksProvider({ children }) {
  const [palette, setPaletteState] = useState(() => localStorage.getItem('sb-palette') || 'warm');
  const [density, setDensityState] = useState(() => localStorage.getItem('sb-density') || 'regular');
  const [nav, setNavState]         = useState(() => localStorage.getItem('sb-nav') || 'side');

  useEffect(() => { localStorage.setItem('sb-palette', palette); }, [palette]);
  useEffect(() => { localStorage.setItem('sb-density', density); }, [density]);
  useEffect(() => { localStorage.setItem('sb-nav', nav); }, [nav]);

  const setPalette = (p) => { if (PALETTES.includes(p)) setPaletteState(p); };
  const setDensity = (d) => { if (DENSITIES.includes(d)) setDensityState(d); };
  const setNav     = (n) => { if (NAV_MODES.includes(n)) setNavState(n); };

  return (
    <TweaksContext.Provider value={{ palette, setPalette, density, setDensity, nav, setNav, PALETTES, DENSITIES, NAV_MODES }}>
      {children}
    </TweaksContext.Provider>
  );
}

export const useTweaks = () =>
  useContext(TweaksContext) || {
    palette: 'warm', density: 'regular', nav: 'side',
    setPalette: () => {}, setDensity: () => {}, setNav: () => {},
    PALETTES, DENSITIES, NAV_MODES,
  };
